import {SMS_CONSENT_ATTESTATION_TEXT} from "@mytour/shared";
import dayjs from "dayjs";
import type {Control, FieldErrors} from "react-hook-form";
import {Controller, useWatch} from "react-hook-form";
import {Button, FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";
import {useReminderDatesPreview} from "../query/reminderQueries.js";
import {type RelaunchCount, buildRelaunchPhrase} from "../relaunchDateProjection.js";

export const relaunchFormSchema = z
  .object({
    scheduledDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Choisissez une date valide.")
      .refine(
        (date) => dayjs(date).isValid() && dayjs(date).format("YYYY-MM-DD") === date,
        "Choisissez une date valide."
      ),
    remainingRelaunches: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    channelEmail: z.boolean(),
    channelSms: z.boolean(),
    smsConsentAttested: z.boolean(),
  })
  .superRefine((data, context) => {
    if (!data.channelEmail && !data.channelSms) {
      context.addIssue({
        code: "custom",
        path: ["channelEmail"],
        message: "Choisissez au moins un canal.",
      });
    }
    if (data.channelSms && !data.smsConsentAttested) {
      context.addIssue({
        code: "custom",
        path: ["smsConsentAttested"],
        message: "Confirmez l’accord de votre client pour programmer une relance par SMS.",
      });
    }
  });
export type RelaunchFormData = z.infer<typeof relaunchFormSchema>;

export function getRelaunchDefaultValues(reminderMonths: readonly number[]): RelaunchFormData {
  return {
    scheduledDate: dayjs()
      .add(reminderMonths[0] ?? 6, "month")
      .format("YYYY-MM-DD"),
    remainingRelaunches: 0,
    channelEmail: true,
    channelSms: false,
    smsConsentAttested: false,
  };
}

type Props = {
  control: Control<RelaunchFormData>;
  errors: FieldErrors<RelaunchFormData>;
  disabled: boolean;
  reminderMonths: readonly number[];
  customerEmail: string | null;
  customerPhone: string | null;
  smsDisabledReason: string | null;
};

const RELAUNCH_COUNT_OPTIONS: {value: RelaunchCount; label: string}[] = [
  {value: 0, label: "Non"},
  {value: 1, label: "1 fois"},
  {value: 2, label: "2 fois"},
];

export function RelaunchForm({
  control,
  errors,
  disabled,
  reminderMonths,
  customerEmail,
  customerPhone,
  smsDisabledReason,
}: Props) {
  const scheduledDate = useWatch({control, name: "scheduledDate"});
  const remainingRelaunches = useWatch({control, name: "remainingRelaunches"});
  const channelSms = useWatch({control, name: "channelSms"});
  const {previewDates, isPreviewLoading, previewError} = useReminderDatesPreview({
    scheduledDate,
    remainingRelaunches,
    channelSms,
  });

  return (
    <>
      <Label className="fw-bold">Dans</Label>
      <Controller
        name="scheduledDate"
        control={control}
        render={({field}) => (
          <>
            <div className="d-flex gap-2 mb-2">
              {reminderMonths.map((months) => (
                <Button
                  key={months}
                  size="sm"
                  color={isSelectedMonths(field.value, months) ? "primary" : "secondary"}
                  outline={!isSelectedMonths(field.value, months)}
                  type="button"
                  disabled={disabled}
                  onClick={() => field.onChange(dayjs().add(months, "month").format("YYYY-MM-DD"))}
                >
                  + {months} mois
                </Button>
              ))}
            </div>
            <FormGroup floating>
              <Input
                type="date"
                id="reminder-scheduled-date"
                placeholder="Date du rappel"
                value={field.value}
                disabled={disabled}
                onChange={(e) => field.onChange(e.target.value)}
              />
              <Label for="reminder-scheduled-date">Date du rappel</Label>
            </FormGroup>
          </>
        )}
      />

      <Label className="fw-bold mt-2">Canal</Label>
      <Controller
        name="channelEmail"
        control={control}
        render={({field}) => (
          <FormGroup check>
            <Input
              id="reminder-email"
              type="checkbox"
              checked={field.value}
              disabled={disabled}
              onChange={(event) => field.onChange(event.target.checked)}
            />
            <Label check for="reminder-email">
              Email{customerEmail ? ` · ${customerEmail}` : ""}
            </Label>
          </FormGroup>
        )}
      />
      {!customerEmail && (
        <p className="text-warning">
          Aucune adresse email : l’envoi échouera si elle reste absente au moment de la relance.
        </p>
      )}
      <Controller
        name="channelSms"
        control={control}
        render={({field}) => (
          <FormGroup check>
            <Input
              id="reminder-sms"
              type="checkbox"
              checked={field.value}
              disabled={disabled || smsDisabledReason !== null}
              onChange={(event) => field.onChange(event.target.checked)}
            />
            <Label check for="reminder-sms">
              SMS{customerPhone ? ` · ${customerPhone}` : ""}
            </Label>
          </FormGroup>
        )}
      />
      {smsDisabledReason && <p className="text-muted">{smsDisabledReason}</p>}
      {errors.channelEmail && (
        <p role="alert" className="text-danger">
          {errors.channelEmail.message}
        </p>
      )}
      {channelSms && (
        <div className="ms-4 mt-2 mb-3">
          <p className="small text-muted mb-2">
            Chaque SMS est facturé en supplément de votre abonnement.
          </p>
          <Controller
            name="smsConsentAttested"
            control={control}
            render={({field}) => (
              <FormGroup check>
                <Input
                  id="reminder-consent"
                  type="checkbox"
                  checked={field.value}
                  disabled={disabled}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
                <Label check for="reminder-consent">
                  {SMS_CONSENT_ATTESTATION_TEXT}
                </Label>
              </FormGroup>
            )}
          />
          {errors.smsConsentAttested && (
            <p role="alert" className="text-danger mb-0 mt-2">
              {errors.smsConsentAttested.message}
            </p>
          )}
        </div>
      )}

      <Label className="fw-bold mt-2">Relancer encore</Label>
      <Controller
        name="remainingRelaunches"
        control={control}
        render={({field}) => (
          <div className="d-flex gap-2">
            {RELAUNCH_COUNT_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                color={field.value === option.value ? "primary" : "secondary"}
                outline={field.value !== option.value}
                type="button"
                disabled={disabled}
                onClick={() => field.onChange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}
      />

      {errors.scheduledDate && (
        <p role="alert" className="text-danger">
          {errors.scheduledDate.message}
        </p>
      )}
      {isPreviewLoading && <p role="status">Calcul des dates…</p>}
      {previewError && (
        <p role="alert" className="text-danger">
          Impossible de prévisualiser les dates. Elles seront calculées à la programmation.
        </p>
      )}
      {previewDates && (
        <div className="alert alert-light mt-2 mb-0">
          {buildRelaunchPhrase(previewDates.map((date) => dayjs(date)))}
        </div>
      )}
    </>
  );

  function isSelectedMonths(currentScheduledDate: string, months: number): boolean {
    return currentScheduledDate === dayjs().add(months, "month").format("YYYY-MM-DD");
  }
}
